import React, { useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { FiUser, FiLogOut, FiUserCheck, FiBriefcase, FiUsers, FiSettings, FiX, FiMessageSquare, FiBarChart2, FiCpu, FiZap } from "react-icons/fi";
import toast from "react-hot-toast";

import api from "@/services/api";
import useStore from "@/services/store";

const Brand = () => {
  const location = useLocation();
  const { user } = useStore();
  const path = location.pathname;

  const getConfig = () => {
    if (path.startsWith("/admin")) return { label: "Admin", url: "/admin" };
    if (path.startsWith("/recruiter")) return { label: "Recruiter", url: "/recruiter" };
    if (path.startsWith("/agency")) return { label: "Agency", url: "/agency" };
    if (path.startsWith("/candidate")) return { label: "Candidate", url: "/candidate" };
    if (path.startsWith("/chat")) return { label: "Chat", url: "/" };
    // Fallback to user's primary role
    if (user?.role_admin) return { label: "Admin", url: "/admin" };
    if (user?.role_recruiter) return { label: "Recruiter", url: "/recruiter" };
    if (user?.role_agency) return { label: "Agency", url: "/agency" };
    if (user?.role_candidate) return { label: "Candidate", url: "/candidate" };
    return { label: "", url: "/" };
  };

  const { label, url } = getConfig();

  return (
    <Link to={url} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
      <span className="text-xl font-extrabold text-gray-900">Jobego</span>
      {label && <span className="text-sm text-gray-500 font-medium">{label}</span>}
    </Link>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  const { user, setUser, impersonation, setImpersonation, organization } = useStore();

  useEffect(() => {
    i18n.changeLanguage(user.language);
  }, []);

  const handleLogout = async () => {
    try {
      await api.post("/user/logout");
      navigate("/auth/signin");
      setUser(null);
      api.setToken(null);
      toast.success("Logged out successfully");
    } catch (e) {
      console.error("Logout failed:", e);
      toast.error(e.code);
    }
  };

  const handleExitImpersonation = () => {
    setImpersonation(null);
    setUser(null);
    api.setToken(null);
    navigate("/admin");
    window.location.reload();
  };

  return (
    <header className="h-16 fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200">
      <div className="h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Brand />

        <div className="flex items-center gap-4">
          {/* Impersonation Indicator */}
          {impersonation && (
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-blue-600">{organization?.name ? `Viewing as user (${organization.name})` : "Viewing as user"}</span>
              </div>
              <button onClick={handleExitImpersonation} className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors">
                <FiX size={14} />
                <span>Exit</span>
              </button>
            </div>
          )}

          <Menu>
            <MenuButton className="flex items-center justify-center w-9 h-9 rounded-full overflow-hidden transition-all focus-visible:outline-none">
              <span className="sr-only">Open user menu</span>
              {user?.avatar || user?.picture ? (
                <img src={user.avatar || user.picture} alt={user.firstname || user.name || "User"} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#6C4CFF] flex items-center justify-center text-white font-semibold text-sm">
                  {(user?.firstname?.[0] || user?.name?.[0] || "U").toUpperCase()}
                </div>
              )}
            </MenuButton>

            <MenuItems
              transition
              anchor="bottom end"
              className="w-72 bg-white rounded-xl border border-gray-200 focus-visible:outline-none shadow-xl py-2 z-50 mt-2 data-closed:scale-95 data-closed:opacity-0"
            >
              {/* User Info Section */}
              <div className="px-4 py-4 border-b border-gray-200">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full overflow-hidden mb-3">
                    {user?.avatar || user?.picture ? (
                      <img src={user.avatar || user.picture} alt={user.firstname || user.name || "User"} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#6C4CFF] flex items-center justify-center text-white font-bold text-xl">
                        {(user?.firstname?.[0] || user?.name?.[0] || "U").toUpperCase()}
                      </div>
                    )}
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-0.5">{user?.firstname && user?.lastname ? `${user.firstname} ${user.lastname}` : user?.name || "User"}</h3>
                  <p className="text-sm text-gray-600">{user?.email || ""}</p>
                  {organization?.name && <p className="text-xs text-gray-500 mt-1 font-medium">{organization.name}</p>}
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                {(user?.role_admin || user?.role_recruiter) && (
                  <MenuItem className="border-b border-gray-200">
                    {({ focus }) => (
                      <NavLink
                        to="/recruiter/account"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors ${focus ? "bg-gray-50" : ""} ${isActive ? "font-bold" : "font-normal"}`
                        }
                      >
                        <FiUser className="w-5 h-5 shrink-0" />
                        <span>Account</span>
                      </NavLink>
                    )}
                  </MenuItem>
                )}

                {(user?.role_admin || user?.role_recruiter) && (
                  <MenuItem className="border-b border-gray-200">
                    {({ focus }) => (
                      <NavLink
                        to="/recruiter/mcp"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors ${focus ? "bg-gray-50" : ""} ${isActive ? "font-bold" : "font-normal"}`
                        }
                      >
                        <FiCpu className="w-5 h-5 shrink-0" />
                        <span>MCP</span>
                      </NavLink>
                    )}
                  </MenuItem>
                )}

                {(user?.role_admin || user?.role_recruiter) && (
                  <MenuItem className="border-b border-gray-200">
                    {({ focus }) => (
                      <NavLink
                        to="/recruiter/brain"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors ${focus ? "bg-gray-50" : ""} ${isActive ? "font-bold" : "font-normal"}`
                        }
                      >
                        <FiZap className="w-5 h-5 shrink-0" />
                        <span>Brain</span>
                      </NavLink>
                    )}
                  </MenuItem>
                )}

                {(user?.role_admin || user?.role_recruiter) && (
                  <MenuItem className="border-b border-gray-200">
                    {({ focus }) => (
                      <NavLink
                        to="/recruiter/usage"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors ${focus ? "bg-gray-50" : ""} ${isActive ? "font-bold" : "font-normal"}`
                        }
                      >
                        <FiBarChart2 className="w-5 h-5 shrink-0" />
                        <span>AI Usage</span>
                      </NavLink>
                    )}
                  </MenuItem>
                )}

                {user?.role_candidate && (
                  <MenuItem className="border-b border-gray-200">
                    {({ focus }) => (
                      <NavLink
                        to="/candidate"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors ${focus ? "bg-gray-50" : ""} ${isActive ? "font-bold" : "font-normal"}`
                        }
                      >
                        <FiUserCheck className="w-5 h-5 shrink-0" />
                        <span>Candidate</span>
                      </NavLink>
                    )}
                  </MenuItem>
                )}

                {user?.role_recruiter && (
                  <MenuItem className="border-b border-gray-200">
                    {({ focus }) => (
                      <NavLink
                        to="/recruiter"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors ${focus ? "bg-gray-50" : ""} ${isActive ? "font-bold" : "font-normal"}`
                        }
                      >
                        <FiBriefcase className="w-5 h-5 shrink-0" />
                        <span>Recruiter</span>
                      </NavLink>
                    )}
                  </MenuItem>
                )}

                {user?.role_agency && (
                  <MenuItem className="border-b border-gray-200">
                    {({ focus }) => (
                      <NavLink
                        to="/agency"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors ${focus ? "bg-gray-50" : ""} ${isActive ? "font-bold" : "font-normal"}`
                        }
                      >
                        <FiUsers className="w-5 h-5 shrink-0" />
                        <span>Agency</span>
                      </NavLink>
                    )}
                  </MenuItem>
                )}

                {user?.role_admin && (
                  <MenuItem className="border-b border-gray-200">
                    {({ focus }) => (
                      <NavLink
                        to="/admin"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors ${focus ? "bg-gray-50" : ""} ${isActive ? "font-bold" : "font-normal"}`
                        }
                      >
                        <FiSettings className="w-5 h-5 shrink-0" />
                        <span>Admin</span>
                      </NavLink>
                    )}
                  </MenuItem>
                )}
              </div>

              {/* Logout */}
              <div className="mt-1 pt-1">
                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={handleLogout}
                      className={`w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors ${focus ? "bg-gray-50" : ""}`}
                    >
                      <FiLogOut className="w-5 h-5 shrink-0" />
                      <span>Logout</span>
                    </button>
                  )}
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        </div>
      </div>
    </header>
  );
};

export default Header;
